import {
  useRef,
  useState,
  useEffect,
  FC,
  ChangeEvent,
  MouseEventHandler,
  useCallback,
} from 'react';

import debounce from 'lodash/debounce';
import { InputRef } from 'antd/es/input';

import { AirportSearchViewModel } from '@api/index';
import { FlightSearchInputDrawerType } from '@components/views/flight/types';
import ResponsiveApplyButton from '@components/views/components/listing/drawer/ResponsiveApplyButton/ResponsiveApplyButton';
import ResponsiveSearchInputDrawer from '@components/views/components/listing/drawer/ResponsiveSearchInputDrawer/ResponsiveSearchInputDrawer';
import { LocationAnimationErrorBox } from '@components/views/components/listing/ListingStyle';
import CustomIcon, { SeturIcon } from '@components/utils/CustomIcon';
import { PastSearchesHeader } from '@components/views/landing/components/booker/common/BookerSearchDrawer';
import { AirPortTypes } from '@domain/flight/searchBar/airportAutoComplate';
import { callService } from '@api/caller';
import { Search2Line } from '@assets/icons';
import { POPULAR_FLIGHTS } from '@components/views/flight/constants';

import {
  ListItemsWrappedByHeader,
  ResponsiveLocationItems,
  ResponsiveLocationListItems,
  DrawerWrapper,
  DrawerFooterWrapper,
  ListingSearchInput,
} from './styled';
import LocationResultItem from './LocationResultItem';

type CustomHeaderType =
  | React.Component<{}, {}, any>
  | React.ReactElement<any, any>;

const ResponsiveAirportSearchDrawer: FC<
  {
    showLocationModal?: () => void;
    callBack?: (item: AirportSearchViewModel) => void;
    onRemoveItem?: (item: AirportSearchViewModel) => void;
    onClearClick?: MouseEventHandler<HTMLButtonElement> | undefined;
    pastSearchesData?: AirportSearchViewModel[];
    onAirportSelect?: (airport: AirportSearchViewModel) => void;
    handleCloseLocationSearch?: () => void;
    handleUpdateButton?: (location: AirportSearchViewModel) => void;
    openAutoCompleteResultModal?: boolean;
    setOpenAutoCompleteResultModal?: React.Dispatch<
      React.SetStateAction<boolean>
    >;
    open?: boolean;
    error?: boolean;
    headerComponent?: CustomHeaderType;
    locationAirport?: AirportSearchViewModel | null | undefined;
  } & FlightSearchInputDrawerType
> = ({
  onAirportSelect,
  title,
  subTitle,
  placeholder,
  drawerTitle,
  icon,
  pastSearchesData,
  onClearClick,
  onRemoveItem,
  setOpenAutoCompleteResultModal,
  openAutoCompleteResultModal,
  headerComponent,
  locationAirport,
  open,
  callBack,
  handleUpdateButton,
  error,
}) => {
  return (
    <ResponsiveSearchInputDrawer
      icon={icon}
      title={title}
      subTitle={subTitle}
      placeholder={placeholder}
      drawerTitle={drawerTitle}
      error={error}
      inputComponent='setur'
    >
      {(hideModal) => (
        <ResponsiveAirportSearch
          onAirportSelect={onAirportSelect}
          hideModal={hideModal}
          title={title as string}
          icon={Search2Line}
          pastSearchesData={pastSearchesData}
          onClearClick={onClearClick}
          onRemoveItem={onRemoveItem}
          setOpenAutoCompleteResultModal={setOpenAutoCompleteResultModal}
          openAutoCompleteResultModal={openAutoCompleteResultModal}
          open={open}
          headerComponent={headerComponent}
          locationAirport={locationAirport}
          callBack={callBack}
          handleUpdateButton={handleUpdateButton}
        />
      )}
    </ResponsiveSearchInputDrawer>
  );
};

const ResponsiveAirportSearch: FC<{
  data?: AirPortTypes;
  showLocationModal?: () => void;
  onAirportSelect?: (airport: AirportSearchViewModel) => void;
  title: string;
  icon: SeturIcon;
  hideModal: () => void;
  callBack?: (item: AirportSearchViewModel) => void;
  onRemoveItem?: (item: AirportSearchViewModel) => void;
  onClearClick?: MouseEventHandler<HTMLButtonElement> | undefined;
  pastSearchesData?: AirportSearchViewModel[];
  handleCloseLocationSearch?: () => void;
  handleUpdateButton?: (location: AirportSearchViewModel) => void;
  openAutoCompleteResultModal?: boolean;
  setOpenAutoCompleteResultModal?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  locationAirport: AirportSearchViewModel | null | undefined;
  open?: boolean;
  headerComponent?: CustomHeaderType;
}> = ({
  onAirportSelect,
  hideModal,
  icon,
  callBack,
  onRemoveItem,
  onClearClick,
  pastSearchesData,
  setOpenAutoCompleteResultModal,
  headerComponent,
  locationAirport,
  handleUpdateButton,
}) => {
  const [airportSearchText, setAirportSearchText] = useState<string>(
    locationAirport &&
      locationAirport?.airportName &&
      locationAirport?.airportCode
      ? locationAirport.airportName + ', ' + locationAirport.airportCode
      : ''
  );

  const [selectedAirport, setSelectedAirport] =
    useState<AirportSearchViewModel>();

  const [filterResult, setFilterResult] = useState<AirportSearchViewModel[]>(
    []
  );
  const [isAirportResultsLoading, setIsAirportResultsLoading] = useState(false);
  const [showList, setShowList] = useState(true);
  const inputRef = useRef<InputRef>(null);
  const [hasError, setHasError] = useState(false);

  const searchAirport = useCallback(
    debounce((s: string) => fetchData(s), 500),
    []
  );

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (airportSearchText) {
      const isDifferentValue =
        selectedAirport?.airportName !== airportSearchText;
      if (airportSearchText.length > 2 && isDifferentValue)
        searchAirport(airportSearchText);
    } else {
      setFilterResult([]);
    }
  }, [airportSearchText]);

  useEffect(() => {
    if (!open) {
      setAirportSearchText(airportSearchText);
      setSelectedAirport(undefined);
      setHasError(false);
    }
  }, [open]);

  const fetchData = async (s: string) => {
    try {
      setIsAirportResultsLoading(true);
      setIsAirportResultsLoading(isAirportResultsLoading);
      const airports = await callService('SearchesService', 'searchesFlight', {
        s: s,
      });
      setOpenAutoCompleteResultModal?.(true);
      setFilterResult(airports);
    } catch (error) {
      setFilterResult([]);
    } finally {
      setIsAirportResultsLoading(false);
    }
  };

  const autoCompleteListItemClick = (i: AirportSearchViewModel) => {
    setOpenAutoCompleteResultModal?.(false);
    setSelectedAirport(i);
    setAirportSearchText(i.airportName + ', ' + i.airportCode);
    hasError && setHasError(false);
  };

  const onSubmit = () => {
    if (!selectedAirport) {
      setHasError(true);
      return;
    }

    setOpenAutoCompleteResultModal?.(true);
    onChangeAirport?.(selectedAirport as any);
    onAirportSelect?.(selectedAirport as AirportSearchViewModel);
    handleUpdateButton?.(selectedAirport as AirportSearchViewModel);
    hideModal();
  };

  const onChangeAirportSearchText = (e: ChangeEvent<HTMLInputElement>) => {
    setAirportSearchText(e.target.value);
    setShowList(true);
    setShowList(showList);
    if (!e.target.value) {
      setFilterResult([]);
      setSelectedAirport(undefined);
    }
  };

  const onChangeAirport = (item: AirportSearchViewModel) => {
    setAirportSearchText(item?.airportName + ', ' + item?.airportCode);
    setSelectedAirport(item);
    setOpenAutoCompleteResultModal?.(true);
    setShowList(false);
    setHasError(false);
  };

  const onFocusChange = () => {
    setShowList(true);
    setOpenAutoCompleteResultModal?.(true);
  };

  const getListItemsWrappedByHeader = (
    data: any,
    header: CustomHeaderType,
    historyData?: boolean
  ) => (
    <ListItemsWrappedByHeader>
      <>
        {header}
        {getListItems(data, historyData)}
      </>
    </ListItemsWrappedByHeader>
  );

  const getListItems = (data: any, historyData?: boolean) => {
    const handleItemClick = (item: AirportSearchViewModel) => {
      autoCompleteListItemClick(item);
      historyData && callBack?.(item);
    };

    return (
      <ResponsiveLocationListItems>
        {Array.isArray(data)
          ? data.map((i: AirportSearchViewModel) => (
              <LocationResultItem
                key={i.airportId}
                locationData={i}
                onClick={() => handleItemClick(i)}
                isHistoryData={!!historyData}
                onRemoveHistoryData={
                  onRemoveItem ? () => onRemoveItem(i) : undefined
                }
              />
            ))
          : null}
      </ResponsiveLocationListItems>
    );
  };

  const showPastSearch =
    ((Array.isArray(pastSearchesData) && pastSearchesData?.length > 0) ||
      (Array.isArray(POPULAR_FLIGHTS) && POPULAR_FLIGHTS?.length > 0)) &&
    airportSearchText.length === 0
      ? true
      : false;

  const showFilteredItems = airportSearchText.length > 2;
  return (
    <>
      <DrawerWrapper>
        <LocationAnimationErrorBox hasError={hasError}>
          <ListingSearchInput
            prefix={
              <CustomIcon icon={icon} paletteColor='grey500' fontSize={20} />
            }
            placeholder='Şehir ya da havalimanı arayın'
            value={airportSearchText}
            onChange={onChangeAirportSearchText}
            ref={inputRef}
            allowClear
            onFocus={onFocusChange}
          />
          {/* <ResponsiveSearchDrawerItem
            icon={icon}
            title={title}
            placeholder='Havalimanı veya şehir'
            subTitle={
              <SearchInput
                ref={inputRef as any}
                aria-label='search-input'
                placeholder='Havalimanı veya şehir'
                value={airportSearchText}
                onFocus={onFocusChange}
                onChange={onChangeAirportSearchText}
                bordered={false}
                allowClear
              />
            }
            className='responsiveAirportSearch__input'
          /> */}
        </LocationAnimationErrorBox>
      </DrawerWrapper>
      <ResponsiveLocationItems>
        {showPastSearch ? (
          <>
            {pastSearchesData?.length
              ? getListItemsWrappedByHeader(
                  pastSearchesData,
                  <PastSearchesHeader onClearClick={onClearClick} />,
                  true
                )
              : null}
            {POPULAR_FLIGHTS?.length
              ? getListItemsWrappedByHeader(
                  POPULAR_FLIGHTS,
                  <PastSearchesHeader
                    title='Popüler Aramalar'
                    iconType='popular'
                  />
                )
              : null}
          </>
        ) : null}
        {showFilteredItems
          ? headerComponent
            ? getListItemsWrappedByHeader(filterResult, headerComponent)
            : getListItems(filterResult)
          : null}
      </ResponsiveLocationItems>

      <DrawerFooterWrapper>
        <ResponsiveApplyButton onClick={onSubmit} isTravelPlanning />
      </DrawerFooterWrapper>
    </>
  );
};

export default ResponsiveAirportSearchDrawer;
